<script lang="ts" setup>
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { usePdfStore } from '@autobid/nuxt-pinia-store/store/usePdfStore.js'
import { useSetSentryUser } from '@autobid/sentry/src/composables/useSetSentryUser'
import CommonErrorBoundary from '@autobid/ui/components/common/CustomErrorBoundary.vue'
import { useAutomaticallyRefreshAuction } from '@autobid/ui/composables/useAutomaticallyRefreshAuction'
import { useBody } from '@autobid/ui/composables/useBody'
import { SHOULD_USE_GLOBAL_BOUND_KEY } from '@autobid/ui/constants/SHOULD_USE_GLOBAL_BOUND_KEY'
import { randomDebounce } from '@autobid/ui/utils/randomDebounce'
import { onMounted, provide, ref, watch } from 'vue'
import { usePdfQuery } from '../../composables/usePdfQuery'
import ErrorPage from './ErrorPage.vue'

const runtimeConfig = useRuntimeConfig().public

useHead({
  link: [
    { rel: 'preconnect', href: runtimeConfig.SELF_URL },
  ],
})

const { shouldGenerate: shouldGeneratePdf } = usePdfQuery()
const { pdfDialogs } = storeToRefs(usePdfStore())
const { closePdfDialog } = usePdfStore()
const setSentryUser = useSetSentryUser()
const { isAuthed } = useAutobidAuth()
const { data: sessionData, getSession } = useAuth()
const INFO = runtimeConfig.AUTOBID_WEBSOCKET?.INFO

if (isAuthed.value && process.client) {
  await getSession()
}

setSentryUser()
useAutomaticallyRefreshAuction()

const shouldUseGlobalErrorBoundary = ref(true)
provide(SHOULD_USE_GLOBAL_BOUND_KEY, shouldUseGlobalErrorBoundary)

useBody()

function refreshToken() {
  if (!process.client || !isAuthed.value || !sessionData.value)
    return

  const diffInMs = sessionData.value.tokenExpires
    ? new Date(sessionData.value.tokenExpires).getTime() - new Date().getTime()
    : 2000

  // set random time of refreshing token in case of using multiple browser tabs (to avoid sending duplicated requests)
  randomDebounce(diffInMs - 1000 * 30, diffInMs - 1000 * 10, () => {
    // eslint-disable-next-line no-console
    console.log('refresh', new Date().toUTCString())
    getSession()
  })
}

watch([() => sessionData.value?.tokenExpires, isAuthed], () => {
  if (!sessionData.value?.tokenExpires)
    return

  refreshToken()
})

onMounted(refreshToken)
</script>

<template>
  <CommonErrorBoundary>
    <template #default>
      <div class="w-full">
        <NuxtLayout>
          <div class="app-wrapper" :data-pdf-mode="shouldGeneratePdf ? 'true' : 'false'">
            <NuxtLoadingIndicator color="var(--color-primary)" />
            <LazyCommonStrapiPdfPage v-if="shouldGeneratePdf" />
            <NuxtPage v-if="!shouldGeneratePdf" />
          </div>
        </NuxtLayout>
      </div>

      <LazyCommonDialogRequestError />
      <LazyCommonExternalLinkDialog />
      <LazyCommonStrapiPopup />
      <client-only>
        <LazyCommonDialogNotSupportedBrowser />
      </client-only>
      <LazyCommonDialogDisabledJavascript />
      <LazyCommonMeilisearchPageBrowser />
      <LazyCommonLoader />
      <LazySectionsSocketDebugger v-if="Number(INFO)" />
      <LazyCommonDialogPdfDialog
        v-for="value in pdfDialogs"
        :key="value.id"
        :is-open="value.isOpen"
        :pdf-url="value.pdfUrl"
        @close="closePdfDialog(value.id)"
      />
      <LazyCommonNotivueInit />
    </template>

    <template #error="{ error }">
      <ErrorPage :error="error" />
      <LazyCommonDialogDisabledJavascript />
    </template>
  </CommonErrorBoundary>
</template>
